import { chakra } from "@chakra-ui/react";
import type { SystemStyleObject } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useEffect, useId, useRef } from "react";
import { dsTag } from "./dsTag";
import { governMotion } from "./frameGovernor";

/**
 * LunaMark — canonical animated Luna state indicator (src/ui/ds).
 *
 * Hierarchy: Feedback → status indicator (the living Luna brand mark).
 * Origin: design handover (agent animations / luna.html) ported and grown
 * through the luna-mark pilot; geometry, colour and timing are Ergert's —
 * read this whole block before retuning anything.
 *
 * ── ANATOMY ────────────────────────────────────────────────────────────
 * A stack of overlapping 120-viewBox SVGs under one <span> root (see law 7),
 * three layers, every state = the SAME geometry in a different pose
 * (transitions morph, never cut):
 * · the BALL (.orb-body → .disc): gradient orb + axial surface spin (7s,
 *   constant in every state), sized per state via .orb-body scale.
 * · the NEGATIVE SPACE (.phase): ONE construction — half-cover rect
 *   (.phase-half) + terminator ellipse (.phase-term), duplicated for
 *   penumbra/core softness, + earthshine lit-shape (.phase-half-inv) — in
 *   an SVG alpha mask over the disc. It forms the acting/scheduled lunar
 *   cycle AND every eclipse pose. The mask genuinely cuts to transparent,
 *   so the mark survives any background.
 * · the OUTER RING (.halo-spin → .halo-luna gradient ring / .halo-sent
 *   sentiment ring): spinning corona; layers crossfade for sentiments.
 *
 * ── THE LAWS (violating any of these has caused visible regressions) ───
 * 1. ONE BALL. Sentiments recolour THE ball (.tint + --sc); nothing ever
 *    paints a second ball. Only waiting/blocked eclipse it.
 * 2. THE SPIN IS SACRED. The ball's 7s surface spin is never paused,
 *    retimed or reset by any state change.
 * 3. THE NEGATIVE SPACE LIVES TO THE RIGHT. It advances from and retreats
 *    to the right; centre-origin iris grows/shrinks are forbidden.
 * 4. RIDES OVER FADES. Any move between a phase pose and a clean disc
 *    rides the lunar clock (see below) — never a fade or a rigid slide of
 *    the dark. Mid-motion rides may REWIND if that's the shorter path;
 *    rides from a resting disc always go FORWARD (law 3).
 * 5. THE OUTER RING NEVER JUMPS. Per-state spin durations re-map the CSS
 *    clock; every dataset flip goes through setDataState(), which
 *    re-anchors the rotation angle and freeze-releases pulse-layer
 *    opacities (a removed pulse animation otherwise blinks to base).
 * 6. WAAPI, NEVER DELAY MATHS. Browsers retain same-name CSS animations
 *    across declaration changes and re-map their clocks when
 *    delay/duration change — landing on arbitrary frames. Every exact
 *    frame is SET via anim.currentTime; rides step it via rAF (reverse
 *    playbackRate throws on infinite animations).
 * 7. CONTINUOUS ANIMATIONS RIDE HTML, NEVER SVG. Chrome will not run a
 *    transform animation on an SVG element on the compositor — not on an
 *    <svg> root, not on a <g>, and no amount of will-change changes it. The
 *    two always-running motions (.surface scroll, .halo-spin rotation) live
 *    on <span> wrappers with static svg content inside, which is why the
 *    mark is a stack of svgs rather than one document. Move either back onto
 *    an svg and every mounted mark costs a main-thread frame forever — this
 *    was ~20% of a core per tab, on every page, before it was found.
 * 8. NOTHING FREE-RUNS. Any animation at natural speed makes Chrome run the
 *    whole frame pipeline every vsync while the window is FOCUSED (the idle
 *    suspension only covers unfocused) — measured at ~80% renderer / ~54%
 *    GPU-process CPU on the client's playground bench, melting GPUs under
 *    video calls. Every infinite animation is therefore adopted by the
 *    shared frame governor (playbackRate 0, currentTime stepped by one
 *    shared low-rate interval — see ds/frameGovernor). Finite animations
 *    and CSS transitions keep native smoothness. Never add a continuous
 *    animation that the governor cannot see (non-CSS, or outside the root).
 *
 * ── THE CLOCK (all cycle choreography keys off these constants) ────────
 * CYCLE_S 2.2s acting loop · SCHEDULED_S 66s (same keyframes, ~1/min) ·
 * new moon at NEW_MOON_FRAC 46% · crescent rest = CRESCENT_MS 1300 (59%,
 * waxing) · full-moon beat at FULL_FRAC 92% · hurried rides run at
 * RIDE_RATE (3×) normalized to the acting loop, entrances at rate 1.
 * The phaseTerminator keyframes sample the physical rx=36.5·|cos(πs)|
 * curve densely and play back LINEAR — that sinusoid IS the easing
 * (crawls at full/new, fastest at the quarters; per-segment CSS easing
 * reads jagged — don't).
 *
 * ── THE STATE DRIVER (the useEffect) ───────────────────────────────────
 * data-state is set imperatively (never by React) so choreography can
 * freeze/steer around each change. Grammar: cycle→eclipse wipes closed;
 * lit↔eclipse slide in/out (HOME/PARKED share one transform-function
 * list — mismatched lists arc via matrix decomposition); cycle→lit
 * slides the frozen dark off; crescent enters by riding to its frame and
 * leaves by unpausing (or the standard exits); ⟶thinking and
 * crescent→lit ride to full before the ball morphs. Load-in (OPT-IN via
 * the loadIn prop — hero surfaces on the main Luna pages only, NEVER
 * incidental mounts like agent state dots; plays once per mount, .booted
 * guard): ring draws in via dash sweep (1s pen-stroke bezier) while the
 * orb rides the waxing half new→full; eclipse mounts stay covered; boot
 * artifacts live in refs so StrictMode's double-mount can't cancel them —
 * the first real transition sweeps them.
 *
 * ── THE ASTEROID (any state → cancelled) ───────────────────────────────
 * Cancellation is delivered by impact: an Asteroids-style rock tumbles in
 * (FLIGHT_MS 620), smashes the ball into shards + sparks + a shockwave,
 * and the aftermath dust orbits the void as a debris belt that thins
 * mote-by-mote (die clocks 1.2–4.2s + 0.8s burn — everything gone by 5s).
 * Cancelled's canonical resting pose IS the aftermath: VOID + grey corona
 * (no ball), identical whether an asteroid delivered it or the mark
 * mounted there. Sequenced via data-impact on the root ("fly" → "boom" →
 * removed at 5.2s so the belt's animations unmatch — a resting cancelled
 * mark runs ZERO impact animations; the state CSS owns the void). The
 * cancelled data-state flip is DEFERRED to the boom, so the previous
 * state plays untouched through the flight and the corona only greys at
 * impact. Particle vectors ride CSS custom props; the field is a
 * deterministic index hash (stable across renders). Belt clocks are
 * re-zeroed via WAAPI at every boom (law 6 — retained fill-forwards
 * burn-outs otherwise leave a replayed belt instantly dead). The impact
 * is skipped (instant flip) for: mount-in-cancelled, sub-24px dots
 * (sub-pixel theatrics would only delay the status colour),
 * animated={false}, and reduced motion.
 *
 * ── GOTCHAS ────────────────────────────────────────────────────────────
 * · getComputedStyle can lie for animated SVG geometry mid-flight; probe
 *   clocks via el.getAnimations() (exact), not computed styles.
 * · Editing this file needs a full browser reload (Fast Refresh bails).
 * · rAF-driven rides stall in hidden tabs and settle exactly on refocus.
 * · Belt/impact animations must NEVER be unconditional — scoped rules
 *   only ([data-impact="boom"]); an always-on invisible belt cost ~78
 *   animations per mounted mark and lagged whole pages.
 *
 * ── TOKEN NOTES ────────────────────────────────────────────────────────
 * Brand + sentiment colours resolve to theme palette vars (luna.*, and
 * --sc → warning.500 / neutral.500 / success.500). The remaining literals
 * are (a) mask paint — pure #000/#FFF alpha-mask values, not UI colours —
 * (b) the handover's artwork stops (orb shading, pole mid-tone, corona
 * highlight), and (c) choreography durations/easings: the theme has no
 * motion token scale (flagged token gap), and these values are the
 * asset's tuned artwork.
 *
 * ── REFERENCES (call sites at promotion time) ──────────────────────────
 * Luna chat: LunaChatView, LunaWelcome, LunaSetupGate, AddMemoryModal,
 * Setup/StepWelcome, Setup/StepConfirm, LunaPanel/MessageThread.
 * Dashboard: Overview/index, Overview/DynamicWidget.
 * Agents: WorkJobs/components/AgentLunaMark (glyph-state adapter — agent
 * surfaces route through it), TriggerPlanningView, PlanningScreen,
 * ToolPermissionSettings. Playground: designs/luna-mark (harness).
 * Related (not this component): LunaMarkIcon / LunaAgentsMarkIcon (static
 * nav SVGs), LunaAgentsMark (orbital agents-system mark).
 */

export const whenToUse = `Use LunaMark wherever the product shows Luna herself or the live status of something Luna is doing (chat surfaces, agent run states, loaders, and status dots), passing the state that matches the domain (idle for a resting Luna, thinking/acting while working, waiting/needs-input/blocked for holds, done/cancelled for outcomes, scheduled and crescent for resting agent runs). It replaces the static LunaIcon everywhere motion is acceptable and scales from 16px status dots to 160px heroes (strokes self-adjust at ≤24px). Do not use it for the Ploy company logo (Logo/AnimatedLogo), for the agents-system mark on creation surfaces (LunaAgentsMark), in static-only contexts like emails or favicons (use LunaMarkIcon), or as a generic spinner unconnected to Luna. State changes morph: drive one mounted instance through states rather than swapping instances, or the choreography is lost. The load-in entrance is opt-in via the loadIn prop: enable it only on hero surfaces of the main Luna pages, never on incidental mounts like agent state dots or list rows.`;

export type LunaMarkState =
  | "idle"
  | "thinking"
  | "acting"
  | "waiting"
  | "needs-input"
  | "cancelled"
  | "done"
  /** Idle with a wake armed (agents taxonomy): acting's lunar cycle at 6×
   *  the duration, orb pulled in — designed by Ergert in this pilot, not in
   *  the original handover. */
  | "scheduled"
  /** Stuck, needs unsticking (agents taxonomy): an annular eclipse in
   *  needs-input's amber — the ball is an amber hairline ring around the
   *  void, under the amber corona with the attention pulse. Distinct from
   *  needs-input's solid amber ball. Also Ergert's pilot addition. */
  | "blocked"
  /** Idle AGENT RUN (agents taxonomy only — not the Luna chat idle): the orb
   *  rests as a waxing crescent, alive but dormant. Ergert's pilot addition. */
  | "crescent";

export interface LunaMarkProps {
  /** Rendered size in px. At ≤24px the corona strokes bump up so the ring survives. */
  size?: number;
  state?: LunaMarkState;
  /** Accessible label override — defaults to "Luna: <state>". */
  label?: string;
  /** Play the load-in entrance on mount (ring draws in while the orb rides
   *  the waxing half to full). OPT-IN, hero surfaces only — the main Luna
   *  pages. Never enable on incidental mounts (agent state dots, list rows,
   *  badges): dozens of simultaneous entrances read as noise. */
  loadIn?: boolean;
  /** Freeze all motion — the brand orb + ring rendered still. For chrome that
   *  wants the real mark, not a spinning one (nav icons, favicons-in-DOM). */
  animated?: boolean;
}

const STATE_LABELS: Record<LunaMarkState, string> = {
  idle: "Luna: idle",
  thinking: "Luna: thinking",
  acting: "Luna: working",
  waiting: "Luna: waiting",
  "needs-input": "Luna: needs your input",
  cancelled: "Luna: cancelled",
  done: "Luna: done",
  scheduled: "Luna: scheduled",
  blocked: "Luna: blocked",
  crescent: "Luna: idle",
};

/* One full tile (96u of the 120u viewBox) = one revolution of the orb's
   surface. Expressed as a percentage because the surface is an <svg> root, so
   a px length would resolve against CSS pixels rather than user units and the
   tile would scroll the wrong distance at every size but 120px. */
const spinSurface = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-80%); }
`;

/* The lunar cycle (acting + scheduled), built like a real moon: the dark
   region is a half-cover rect plus a terminator ELLIPSE whose rx breathes
   full→0→full — so the terminator is curved in the crescents, straight at
   the quarters, and flips curvature for the gibbous phases, instead of the
   old single-circle sweep. One cycle: full → waning → new → waxing → full,
   with beats on full (92–100%) AND new (44–48%). Every discrete flip lands
   inside a beat where it is invisible: the rect switches sides at new (disc
   fully dark, mid-hold) and at the wrap (disc fully lit); the ellipse flips
   paint at the quarters, where rx≈0 makes it a line. */
const phaseHalf = keyframes`
  0%, 45.99% { x: 60px; width: 44px; }
  46%, 100%  { x: 16px; width: 44px; }
`;
/* rx follows the PHYSICAL terminator curve — the projection of uniform
   rotation, rx = 36.5·|cos(π·s)| — sampled densely so linear timing between
   samples plays back the true sinusoidal velocity: the terminator crawls
   near full/new, moves fastest through the quarters, and never dead-stops
   (per-segment CSS easing stuttered). cx superimposes a smooth sine bump
   toward the dark side mid-crescent (the drawn-crescent offset), back at
   centre for new (46%) and full (92%) so coverage stays total. Fill flips
   land at the quarters, where rx≈0 makes the ellipse a line. */
const phaseTerminator = keyframes`
  0%     { rx: 36.5px;  cx: 60px;    fill: #FFFFFF; }
  4.4%   { rx: 34.71px; cx: 60px; }
  8.8%   { rx: 29.53px; cx: 60px; }
  13.2%  { rx: 21.45px; cx: 60px; }
  17.6%  { rx: 11.28px; cx: 60px; }
  21.99% { fill: #FFFFFF; }
  22%    { rx: 0px;     cx: 60px;    fill: #000000; }
  26.4%  { rx: 11.28px; cx: 63.53px; }
  30.8%  { rx: 21.45px; cx: 65.71px; }
  35.2%  { rx: 29.53px; cx: 65.71px; }
  39.6%  { rx: 34.71px; cx: 63.53px; }
  44%    { rx: 36.5px;  cx: 60px; }
  48%    { rx: 36.5px;  cx: 60px; }
  52.4%  { rx: 34.71px; cx: 56.47px; }
  56.8%  { rx: 29.53px; cx: 54.29px; }
  61.2%  { rx: 21.45px; cx: 54.29px; }
  65.6%  { rx: 11.28px; cx: 56.47px; }
  69.99% { fill: #000000; }
  70%    { rx: 0px;     cx: 60px;    fill: #FFFFFF; }
  74.4%  { rx: 11.28px; cx: 60px; }
  78.8%  { rx: 21.45px; cx: 60px; }
  83.2%  { rx: 29.53px; cx: 60px; }
  87.6%  { rx: 34.71px; cx: 60px; }
  92%    { rx: 36.5px;  cx: 60px; }
  100%   { rx: 36.5px;  cx: 60px;    fill: #FFFFFF; }
`;
/* the earthshine overlay is the LIT shape (the complement construction):
   white rect on the OPPOSITE side + the same terminator ellipse. Blurred
   wide and painted faint, its soft edge IS the terminator — visibility
   peaks there and decays into the dark side, with no phantom second arc.
   Around new (44–48%, the hold) the rect morphs THROUGH full width, so the
   rim glow wraps the whole ghost disc symmetrically and then recedes toward
   the waxing side — instead of teleporting from one limb to the other. */
const inverseHalf = keyframes`
  0%, 41%   { x: 16px; width: 44px; }
  44%, 48%  { x: 16px; width: 88px; }
  51%, 100% { x: 60px; width: 44px; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const glow = keyframes`
  0%, 100% { opacity: 0.45; }
  50%      { opacity: 0.95; }
`;

const attn = keyframes`
  0%, 100% { opacity: 0.5; }
  50%      { opacity: 1; }
`;

/* load-in: the corona draws itself around the orb once (dash sweep over the
   r45 ring's ~283 circumference), while the spin carries the start point */
const ringDraw = keyframes`
  from { stroke-dashoffset: 283; }
  to   { stroke-dashoffset: 0; }
`;

/* ── THE ASTEROID (any state → cancelled) ───────────────────────────────────
   A vector rock (Asteroids-game outline) tumbles in from off-canvas, smashes
   the ball into shards, and the aftermath dust orbits the void as a debris
   belt that thins mote-by-mote — see THE ASTEROID in the header contract. */
const asteroidFly = keyframes`
  0%   { transform: translate(84px, -74px) scale(0); opacity: 0; }
  22%  { opacity: 1; }
  100% { transform: translate(0px, 0px) scale(1); opacity: 1; }
`;
const asteroidTumble = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(660deg); }
`;
const trailFlicker = keyframes`
  0%, 100% { opacity: 0.12; }
  50%      { opacity: 0.7; }
`;
/* per-particle vectors arrive via CSS custom props on the element */
const shardFly = keyframes`
  from { transform: translate(0px, 0px) rotate(0deg) scale(1); opacity: 1; }
  to   { transform: translate(var(--dx), var(--dy)) rotate(var(--dr)) scale(0.5); opacity: 0; }
`;
const sparkFly = keyframes`
  0%   { transform: translate(0px, 0px) scale(1); opacity: 1; }
  65%  { opacity: 0.9; }
  100% { transform: translate(var(--dx), var(--dy)) scale(0.25); opacity: 0; }
`;
const shockwaveOut = keyframes`
  from { transform: scale(0.4); opacity: 0.85; }
  to   { transform: scale(3.1); opacity: 0; }
`;
const debrisOrbit = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;
const dustTwinkle = keyframes`
  0%, 100% { opacity: var(--o); }
  50%      { opacity: calc(var(--o) * 0.3); }
`;
/* each mote burns out on its own clock (--die delay) — the belt thins out
   one particle at a time until the void is clear */
const dustFade = keyframes`
  from { opacity: var(--o); }
  to   { opacity: 0; }
`;

const FLIGHT_MS = 620;

/* deterministic per-index pseudo-random (shader-style hash) so the particle
   field is stable across renders/StrictMode remounts */
const irand = (i: number, salt: number) => {
  const x = Math.sin(i * 127.1 + salt * 311.7) * 43758.5453;
  return x - Math.floor(x);
};

/* the debris belt: orbit radii spread between the orb's old edge (~27 at the
   0.8 body scale) and the outer ring (r45) */
const DUST = Array.from({ length: 34 }, (_, i) => ({
  r: 30 + irand(i, 1) * 12.5,
  s: 0.6 + irand(i, 2) * 1.3,
  dur: 3 + irand(i, 3) * 4,
  frac: irand(i, 4),
  o: 0.4 + irand(i, 5) * 0.55,
  grey: irand(i, 6) > 0.3,
  die: 1.2 + irand(i, 18) * 3,
}));
/* a few chunkier rocks riding the same belt */
const CHUNKS = Array.from({ length: 5 }, (_, i) => ({
  r: 31 + irand(i, 11) * 10,
  dur: 4 + irand(i, 12) * 4,
  frac: irand(i, 13),
  o: 0.5 + irand(i, 14) * 0.4,
  die: 1.4 + irand(i, 19) * 2.8,
  d: ["M0 -2 L1.8 -0.6 L1.2 1.6 L-1.1 1.7 L-1.9 -0.4 Z", "M0 -1.6 L1.5 -1 L1.7 0.9 L-0.4 1.6 L-1.6 0.3 Z"][i % 2],
}));
const SPARKS = Array.from({ length: 14 }, (_, i) => {
  const a = (i / 14) * Math.PI * 2 + irand(i, 7) * 0.5;
  const d = 22 + irand(i, 8) * 16;
  return {
    dx: Math.cos(a) * d,
    dy: Math.sin(a) * d,
    s: 0.7 + irand(i, 9) * 0.9,
    dur: 0.42 + irand(i, 10) * 0.3,
    hot: irand(i, 15) > 0.5,
  };
});
/* shards: chunky fragments of the smashed ball,
   each flung roughly along its sector's outward angle */
const SHARDS = [
  { d: "M0 0 L7 -9 L11 -2 Z", a: -55 },
  { d: "M0 0 L11 -2 L10 6 L4 8 Z", a: 15 },
  { d: "M0 0 L4 8 L-3 10 Z", a: 70 },
  { d: "M0 0 L-3 10 L-9 5 Z", a: 130 },
  { d: "M0 0 L-9 5 L-11 -3 L-5 -6 Z", a: 190 },
  { d: "M0 0 L-5 -6 L-1 -11 L7 -9 Z", a: -100 },
].map((s, i) => {
  const rad = (s.a * Math.PI) / 180;
  const dist = 19 + irand(i, 16) * 12;
  return {
    d: s.d,
    dx: Math.cos(rad) * dist,
    dy: Math.sin(rad) * dist,
    dr: (irand(i, 17) - 0.5) * 320,
  };
});

/**
 * All state posing is CSS keyed off data-state / data-size on the svg root,
 * exactly as in the handover asset, so entries/exits tween between poses.
 * Brand + sentiment colours resolve to theme palette vars; the remaining
 * literal stops (orb shading, pole mid-tone, corona highlight) are the
 * asset's artwork values and have no theme tokens.
 */
/* The lunar-cycle clock, shared between the CSS and the state driver.
   crescent = the acting cycle PAUSED at CRESCENT_MS of its 2.2s loop — just
   past the new moon (NEW_MOON_FRAC), i.e. a genuine waxing crescent lit on
   the right. The driver enters crescent by riding this same clock (let the
   cycle run until it reaches the crescent frame, then stop it) and leaves it
   to a cycle by resuming from that frame. The frame itself is always SET via
   the Web Animations API (anim.currentTime) — never via animation-delay
   arithmetic, because the browser retains a same-name CSS animation across
   declaration changes and re-maps its clock, landing on arbitrary frames. */
const CYCLE_S = 2.2;
/* the cycle's full-moon beat begins at 92% — the load-in rides the waxing
   half (new moon → here) as the entrance */
const FULL_FRAC = 0.92;
/* the scheduled (idle-agent) cycle: one full lunar lap per ~minute — barely
   perceptible drift, alive on a long look. Costs nothing extra: the browser
   repaints animating masks per-frame regardless of cycle length. */
const SCHEDULED_S = 66;
const NEW_MOON_FRAC = 0.46;
const CRESCENT_MS = 1300;
const CRESCENT_FRAC = CRESCENT_MS / (CYCLE_S * 1000);
/* how much faster than the cycle's own speed the ride to the crescent frame
   plays — the mark knows where it's going, so it hurries (and rewinds when
   backwards is the shorter path) */
const RIDE_RATE = 3;

/* law 8 — marks this size and up ride the governor's 24fps tier (arc steps
   get too coarse at big radii); smaller marks take the 12fps base tier */
const HERO_PX = 96;

const lunaSx: SystemStyleObject = {
  display: "block",
  position: "relative",
  overflow: "visible",

  /* The root used to be the <svg> itself — a replaced element, whose flex
     min-width:auto resolves to its intrinsic size, so it held its ground in a
     slot narrower than the requested size (the sidebar hands an 18px mark a
     16px box). A span full of absolutely positioned children has a
     content-based minimum of zero, so it would silently shrink instead and
     render the whole mark undersized. */
  flexShrink: 0,

  /* The mark is a stack of overlapping layers rather than one svg document,
     because Chrome will not run a transform animation on an SVG element on the
     compositor — not on an <svg> root, not on a <g>, however it is hinted. An
     HTML element wrapping static svg content does composite, so each
     always-running animation gets its own span and the svg inside it holds
     still. Keep it that way: moving an animation back onto an svg silently
     costs a main-thread frame for as long as the mark is mounted. */
  "& svg, & span": {
    position: "absolute",
    inset: 0,
    /* follow the box rather than the width/height attributes: a caller can
       squeeze the mark (the sidebar drops an 18px mark into a 16px slot), and
       a fixed px size on an inset-0 layer is over-constrained — it pins left
       instead of filling, which knocks the layers out of register with each
       other once .orb-body scales about the box centre */
    width: "100%",
    height: "100%",
    overflow: "visible",
  },

  /* sentiment colour, overridden per state below */
  "--sc": "var(--chakra-colors-luna-0)",

  /* corona stroke weights — bumped at small sizes so the ring survives */
  "--ring": "3",
  "--glow": "6",
  "--glow-big": "11",
  '&[data-size="sm"]': { "--ring": "6", "--glow": "10", "--glow-big": "16" },

  "& .halo-ring": { strokeWidth: "var(--ring)" },
  "& .halo-glow": { strokeWidth: "var(--glow)" },
  "& .halo-glow-big": { strokeWidth: "var(--glow-big)" },
  /* pulse layers tween their brightness when a pulse animation comes or
     goes — paired with the driver freezing the mid-pulse value at each
     state flip, so a ring never blinks to base before crossfading */
  "& .halo-luna .halo-glow, & .halo-luna .halo-glow-big, & .halo-sent .halo-glow, & .halo-sent .halo-glow-big":
    { transition: "opacity 0.4s ease" },

  /* sentiment palette (handover placeholders → real sentiment tokens);
     blocked shares needs-input's amber */
  '&[data-state="needs-input"], &[data-state="blocked"]': {
    "--sc": "var(--chakra-colors-warning-500)",
  },
  '&[data-state="cancelled"]': { "--sc": "var(--chakra-colors-neutral-500)" },
  '&[data-state="done"]': { "--sc": "var(--chakra-colors-success-500)" },

  /* orb body (disc + phase mask scale together); thinking + scheduled pull in */
  "& .orb-body": {
    /* an HTML wrapper now, not a <g>: the box centre is what 60px 60px meant
       in the 120 viewBox, but as a percentage it holds at every size */
    transformOrigin: "center",
    transform: "scale(0.8)",
    /* opacity carries cancelled's void: the ball melts out entering it and
       back in on the way to any other state */
    transition:
      "transform 0.5s cubic-bezier(0.45, 0.05, 0.25, 1), opacity 0.4s ease",
  },
  '&[data-state="thinking"] .orb-body, &[data-state="scheduled"] .orb-body': {
    transform: "scale(0.6)",
  },
  /* cancelled = VOID + grey corona: the ball is simply gone (asteroid or
     not — one canonical pose, so a just-smashed run and a mounted-cancelled
     list row match). The base 0.4s opacity transition melts it out/in. */
  '&[data-state="cancelled"] .orb-body': { opacity: 0 },

  /* sentiment tint — recolours THE ball with the soft flat finish of the
     old sentiment disc. There is only one ball: done/needs-input tint it at
     full size (cancelled shows no ball at all — void + grey corona). The sphere shading (limb +
     specular) crossfades out under the tint so the coloured ball keeps the
     gentle pastel look rather than a heavy 3D one. */
  "& .tint": { opacity: 0, transition: "opacity 0.45s ease" },
  "& .tint-fill": { fill: "var(--sc)", transition: "fill 0.4s ease" },
  "& .shade-limb, & .shade-spec": { transition: "opacity 0.45s ease" },
  /* blocked keeps the amber tint too, so transitions in/out of it crossfade
     sentiment→sentiment instead of flashing the azurite ball mid-flight */
  '&[data-state="done"] .tint, &[data-state="needs-input"] .tint, &[data-state="blocked"] .tint':
    { opacity: 1 },
  '&[data-state="done"] .shade-limb, &[data-state="needs-input"] .shade-limb, &[data-state="blocked"] .shade-limb, &[data-state="done"] .shade-spec, &[data-state="needs-input"] .shade-spec, &[data-state="blocked"] .shade-spec':
    { opacity: 0 },

  /* thinking: the orb itself glows — a blurred brand-gradient bloom behind
     the disc, pulsing like waiting's ring glow */
  "& .orb-glow": { opacity: 0, transition: "opacity 0.45s ease" },
  '&[data-state="thinking"] .orb-glow': {
    opacity: 0.45,
    animation: `${glow} 1.8s ease-in-out 0.45s infinite`,
  },

  /* axial spin — poles scroll and wrap, clipped to the disc. ONE speed,
     always running, in every state: the ball's rotation is never paused or
     retimed (changing duration re-maps the animation clock = a visible
     jump), so it stays continuous across all state changes. States express
     themselves through size, colour and the negative space — never through
     the spin. */
  "& .surface": {
    animation: `${spinSurface} 7s linear infinite`,
  },

  /* THE negative space — one construction for everything. The same phase
     elements that run the acting/scheduled cycle also form every eclipse:
     at rest the terminator ellipse is an iris — rx 0 (nothing cut) in lit
     states, full cover in the eclipse states — and CSS transitions tween it
     between poses, so every state change moves this one construction (the
     void grows from the heart of the orb / recedes back into it). Fill
     rests black; only the cycle's keyframes flip it. Tilted like a sky moon
     (rotation-proof: the terminator is a circle at rest and at new/full).
     Group opacity 0.95 = the dark always ghosts 5% of the disc. */
  "& .phase": {
    opacity: 0.95,
    transformBox: "view-box",
    transformOrigin: "60px 60px",
    transform: "rotate(11deg)",
  },
  /* rest poses are STATIC (no CSS transitions) — every cross-state motion
     is choreographed by the state driver as a directional wipe, slide-off
     or fade; a bare rx tween between rests would grow/shrink the dark at
     the centre, which reads wrong */
  "& .phase-term": {
    fill: "#000000",
    rx: "0px",
    cx: "60px",
  },
  "& .phase-half, & .phase-half-inv": {
    x: "60px",
    width: "0px",
  },
  /* only waiting + blocked are true eclipses now — the tinted states keep
     the ball fully visible */
  '&[data-state="waiting"] .phase-term, &[data-state="blocked"] .phase-term': {
    rx: "36.5px",
  },
  /* crescent (idle agent run) = the acting lunar cycle PAUSED at the
     crescent frame. Same negative space, same clock, just not advancing.
     These rules only park the animations; the driver holds them at
     CRESCENT_MS via the Web Animations API (see the clock constants above)
     on mount and on every entry. */
  '&[data-state="crescent"] .phase-half': {
    animation: `${phaseHalf} ${CYCLE_S}s linear infinite paused`,
  },
  '&[data-state="crescent"] .phase-term': {
    animation: `${phaseTerminator} ${CYCLE_S}s linear infinite paused`,
  },
  '&[data-state="crescent"] .phase-half-inv': {
    animation: `${inverseHalf} ${CYCLE_S}s linear infinite paused`,
  },
  '&[data-state="acting"].run .phase-half': {
    animation: `${phaseHalf} 2.2s linear infinite`,
  },
  '&[data-state="acting"].run .phase-term': {
    animation: `${phaseTerminator} 2.2s linear infinite`,
  },
  '&[data-state="acting"].run .phase-half-inv': {
    animation: `${inverseHalf} 2.2s linear infinite`,
  },
  '&[data-state="scheduled"].run .phase-half': {
    animation: `${phaseHalf} ${SCHEDULED_S}s linear infinite`,
  },
  '&[data-state="scheduled"].run .phase-term': {
    animation: `${phaseTerminator} ${SCHEDULED_S}s linear infinite`,
  },
  '&[data-state="scheduled"].run .phase-half-inv': {
    animation: `${inverseHalf} ${SCHEDULED_S}s linear infinite`,
  },

  /* fade the disc under the settled eclipse so the ball hides COMPLETELY
     (no 5% ghost at rest); blocked's rim is drawn by .sent-ring in amber */
  "& .disc": {
    transition: "opacity 0.2s ease",
    /* the disc's circular edge, formerly the clip- clipPath: r=34 of the 120
       viewBox, so 34/120 of the box */
    clipPath: "circle(28.3333% at 50% 50%)",
  },
  '&[data-state="waiting"] .disc, &[data-state="blocked"] .disc': {
    opacity: 0,
    transition: "opacity 0.2s ease 0.55s",
  },

  /* corona: one spinning ring; luna gradient crossfades to sentiment */
  /* load-in (driver adds .boot while a mount's entrance plays): every corona
     circle draws in as an arc growing to the full ring, with a pen-stroke
     velocity profile — set off slowly, sweep fastest through the middle of
     the circle, and slow again to close it (symmetric easeInOutQuart) */
  "&.boot .halo-spin circle": {
    strokeDasharray: "283",
    animation: `${ringDraw} 1s cubic-bezier(0.76, 0, 0.24, 1) both`,
  },
  /* while booting a non-eclipse mount, the phase parts carry the (paused)
     cycle animations so the driver can ride the waxing half — new moon →
     full — as the entrance: the orb genuinely CYCLES into its full moon,
     with the sinusoidal keyframes supplying the ease. Eclipse mounts stay
     covered (their rest pose), so they are excluded. */
  '&.boot:not([data-state="waiting"]):not([data-state="blocked"]) .phase-half':
    { animation: `${phaseHalf} ${CYCLE_S}s linear infinite paused` },
  '&.boot:not([data-state="waiting"]):not([data-state="blocked"]) .phase-term':
    { animation: `${phaseTerminator} ${CYCLE_S}s linear infinite paused` },
  '&.boot:not([data-state="waiting"]):not([data-state="blocked"]) .phase-half-inv':
    { animation: `${inverseHalf} ${CYCLE_S}s linear infinite paused` },
  /* .halo-spin is now an HTML wrapper around the corona's svg, so the default
     transform-box (its border box) already centres on the mark and no fill-box
     bbox resolution is needed. The corona's circles are all centred on the
     viewBox, so turning the square wrapper is the rotation the <g> used to do. */
  "& .halo-spin": {
    transformOrigin: "center",
    animation: `${spin} 6s linear infinite`,
  },
  '&[data-state="thinking"] .halo-spin': { animationDuration: "2.4s" },
  '&[data-state="waiting"] .halo-spin': { animationDuration: "3.6s" },

  "& .halo-luna": { opacity: 1, transition: "opacity 0.45s ease" },
  '&[data-state="needs-input"] .halo-luna, &[data-state="cancelled"] .halo-luna, &[data-state="done"] .halo-luna, &[data-state="blocked"] .halo-luna':
    { opacity: 0 },

  '&[data-state="waiting"] .halo-luna .halo-glow, &[data-state="waiting"] .halo-luna .halo-glow-big':
    { animation: `${glow} 1.8s ease-in-out infinite` },

  "& .halo-sent": { opacity: 0, transition: "opacity 0.45s ease" },
  "& .halo-sent circle": {
    stroke: "var(--sc)",
    transition: "stroke 0.4s ease",
  },
  '&[data-state="needs-input"] .halo-sent, &[data-state="done"] .halo-sent, &[data-state="blocked"] .halo-sent': {
    opacity: 1,
  },
  '&[data-state="cancelled"] .halo-sent': { opacity: 0.35 },
  '&[data-state="needs-input"] .halo-sent, &[data-state="blocked"] .halo-sent': {
    animation: `${attn} 1s ease-in-out infinite`,
    animationDelay: "0.45s",
  },
  /* done: still ready to go — the green ring breathes light exactly like
     waiting's luna ring, just calmer; the orb itself stays at rest */
  '&[data-state="done"] .halo-sent .halo-glow, &[data-state="done"] .halo-sent .halo-glow-big': {
    animation: `${glow} 2.6s ease-in-out infinite`,
    animationDelay: "0.45s",
  },

  /* the sentiment layer only draws blocked's amber ANNULUS — a glowing rim
     around the void (layered like the corona: wide bloom + mid glow + crisp
     ring). Every other sentiment recolours THE ball via .tint — no second
     ball is ever painted. */
  "& .sent": {
    opacity: 0,
    transformBox: "view-box",
    transformOrigin: "60px 60px",
    transform: "scale(0.8)",
    transition: "opacity 0.45s ease",
  },
  '&[data-state="blocked"] .sent': { opacity: 1 },
  "& .sent-ring circle": { stroke: "var(--sc)" },

  /* ── the asteroid impact layer ──────────────────────────────────────────
     Sequenced by data-impact on the root: "fly" (rock inbound) → "boom"
     (shatter + burst + belt; dropped once the belt has burned out). No
     data-impact = layer fully dormant, zero animation cost. */
  "& .impact-layer": { pointerEvents: "none" },
  "& .asteroid-flight": { opacity: 0 },
  '&[data-impact="fly"] .asteroid-flight': {
    opacity: 1,
    animation: `${asteroidFly} ${FLIGHT_MS}ms cubic-bezier(0.45, 0, 1, 1) both`,
  },
  '&[data-impact="fly"] .asteroid-tumble': {
    animation: `${asteroidTumble} ${FLIGHT_MS}ms linear both`,
  },
  '&[data-impact="fly"] .asteroid-trail circle': {
    animation: `${trailFlicker} 0.18s linear infinite`,
  },
  /* the strike: rock consumed, ball gone on the spot */
  '&[data-impact="boom"] .orb-body': {
    opacity: 0,
    transition: "opacity 0.08s linear",
  },
  "& .shards path": { opacity: 0 },
  '&[data-impact="boom"] .shards path': {
    animation: `${shardFly} 0.9s cubic-bezier(0.15, 0.6, 0.4, 1) both`,
  },
  "& .sparks circle": { opacity: 0 },
  '&[data-impact="boom"] .sparks circle': {
    animation: `${sparkFly} var(--dur) cubic-bezier(0.1, 0.7, 0.4, 1) both`,
  },
  "& .shockwave": {
    opacity: 0,
    fill: "none",
    transformBox: "view-box",
    transformOrigin: "60px 60px",
  },
  '&[data-impact="boom"] .shockwave': {
    animation: `${shockwaveOut} 0.7s cubic-bezier(0.2, 0.7, 0.4, 1) both`,
  },
  /* the ring reacts only AT the boom: nothing about the mark betrays the
     incoming rock — the deferred data-state flip lands cancelled at impact,
     and the normal state CSS crossfades the corona to grey right then */
  /* aftermath: the belt fades in and keeps orbiting while cancelled holds;
     dropping data-impact (leaving cancelled) fades it back out */
  "& .debris": { opacity: 0, transition: "opacity 0.5s ease" },
  '&[data-impact="boom"] .debris': {
    opacity: 1,
    transition: "opacity 0.9s ease 0.25s",
  },
  /* PERF: belt animations exist ONLY while the belt is visible (the boom
     window). Unconditional orbit/twinkle had every dormant mark running ~78
     invisible animations — 22 specimens ≈ 1700 animations = page-wide lag.
     Once the last mote burns out the driver flips boom → "spent", unmatching
     these rules so a resting cancelled mark runs zero belt animations. */
  "& .dust-orbit": {
    transformBox: "view-box",
    transformOrigin: "60px 60px",
  },
  /* motes REST invisible: when the boom rules unmatch (impact attribute
     dropped after the belt burns out), reverting to base must not repaint
     them — base opacity 1 resurrected the whole belt for the length of the
     group's fade-out (the "line inside the corona" flash) */
  "& .dust-orbit circle, & .dust-orbit path": {
    opacity: 0,
    /* leaving cancelled MID-belt still fades the motes out softly (the
       transition runs from the removed animation's last value); at the
       settle drop they're already at 0, so nothing repaints */
    transition: "opacity 0.4s ease",
  },
  '&[data-impact="boom"] .dust-orbit': {
    animation: `${debrisOrbit} var(--dur) linear infinite`,
  },
  '&[data-impact="boom"] .dust-orbit circle, &[data-impact="boom"] .dust-orbit path': {
    animation: `${dustTwinkle} 2.4s ease-in-out var(--tw, 0s) infinite, ${dustFade} 0.8s ease-out var(--die, 2s) forwards`,
  },

  /* reduced motion: kill continuous spins/pulses; state changes stay legible
     via the (still-instant) colour/size differences */
  "@media (prefers-reduced-motion: reduce)": {
    '& .surface, & .halo-spin, & .phase-half, & .phase-term, & .phase-half-inv, & .halo-luna, & .halo-luna .halo-glow, & .halo-luna .halo-glow-big, & .halo-sent, & .halo-sent .halo-glow, & .halo-sent .halo-glow-big':
      { animation: "none !important" },
    "& .impact-layer": { display: "none" },
  },
  '&[data-static="true"] .impact-layer': { display: "none" },
  /* forced-still variant (animated={false}) — same kill list, for chrome that
     wants the real mark rendered motionless (nav icons) */
  '&[data-static="true"] .surface, &[data-static="true"] .halo-spin, &[data-static="true"] .phase-half, &[data-static="true"] .phase-term, &[data-static="true"] .phase-half-inv, &[data-static="true"] .halo-luna, &[data-static="true"] .halo-luna .halo-glow, &[data-static="true"] .halo-luna .halo-glow-big, &[data-static="true"] .halo-sent, &[data-static="true"] .halo-sent .halo-glow, &[data-static="true"] .halo-sent .halo-glow-big':
    { animation: "none !important" },
};

export const LunaMark = ({
  size = 40,
  state = "idle",
  label,
  loadIn = false,
  animated = true,
}: LunaMarkProps) => {
  const id = useId().replace(/[^a-zA-Z0-9]/g, "");
  /* 120-space → CSS px, for the phase mask: it is referenced from CSS on an
     HTML element, whose userSpaceOnUse coordinates are box pixels */
  const maskScale = size / 120;
  const svgRef = useRef<HTMLSpanElement>(null);
  const runTimer = useRef<number>();
  // data-state is driven imperatively by the state-driver effect below
  // (never by React) so it can freeze/steer the phase pose around changes.
  const initialState = useRef(state);
  // load-in artifacts, cancelled by the first real transition so a state
  // change mid-boot never races the reveal's timers
  const bootTimers = useRef<number[]>([]);
  const bootRide = useRef<(() => void) | undefined>();

  /* State driver — ONE ball, ONE negative space. Sentiments recolour the
     ball (CSS tint); only waiting + blocked eclipse it. Choreography:
     - cycle → eclipse:   the wipe CLOSES (continues to new moon)
     - cycle → thinking, crescent → any lit state: the clock HURRIES to full
       (shortest path mid-motion; strictly forward from rest) so the light
       returns through the phase's own motion — never cuts, never fades
     - cycle → anything else lit (idle/done/cancelled/needs-input/other
       cycle): the frozen negative space SLIDES OFF behind the ball; the
       ball recolours/resizes via CSS at the same time
     - lit → eclipse:     the centred dark SLIDES IN from the right,
       then the disc fade hides the ball completely
     - eclipse → anything: the dark SLIDES back OUT to the right; a cycle
       destination then starts its phases from the right where it went
     Centre-origin grow/shrink (the iris) is deliberately never used, and
     the ball's spin is never touched. */
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const isPhaseState = (s: string | undefined) =>
      s === "acting" || s === "scheduled";
    const isEclipseState = (s: string | undefined) =>
      s === "waiting" || s === "blocked";
    const isPhase = isPhaseState(state);

    const phase = el.querySelector<SVGGElement>(".phase");
    const parts = el.querySelectorAll<SVGGraphicsElement>(
      ".phase-half, .phase-term, .phase-half-inv",
    );
    const terms = el.querySelectorAll<SVGGraphicsElement>(".phase-term");
    /* the negative space's CSS animations via the Web Animations API — the
       one interface whose clock reads and writes are exact, immune to the
       browser re-mapping retained same-name animations */
    /* The Web Animations API is browser-only — jsdom (vitest) ships neither
       Element.getAnimations nor the CSSAnimation constructor. Every driver read
       goes through here so a non-browser DOM degrades to "no animations to
       steer": the SVG still renders at its declared state, it just doesn't
       animate. Without this guard the whole tree throws on render, which takes
       out every test that mounts anything containing a Luna mark. */
    const cssAnimationsOf = (n: Element): CSSAnimation[] => {
      if (
        typeof n.getAnimations !== "function" ||
        typeof CSSAnimation === "undefined"
      )
        return [];
      return n
        .getAnimations()
        .filter((a): a is CSSAnimation => a instanceof CSSAnimation);
    };
    const partAnims = () => Array.from(parts).flatMap((p) => cssAnimationsOf(p));
    const holdCrescent = () =>
      partAnims().forEach((a) => {
        a.pause();
        a.currentTime = CRESCENT_MS;
      });
    /* the corona spins on per-state durations (thinking/waiting hurry it),
       and changing animation-duration re-maps a retained animation's clock —
       a visible jolt of the ring. Every dataset flip goes through here so
       the spin is re-anchored at its current angle: the outer ring NEVER
       jumps, it only eases into its new pace. */
    const SPIN_S: Record<string, number> = { thinking: 2.4, waiting: 3.6 };
    const spins = el.querySelectorAll<HTMLElement>(".halo-spin");
    /* every layer a state can PULSE (glow/attn animations): a flip that
       removes the animation would snap them to base brightness mid-pulse,
       so setDataState freezes their current opacity across the flip and
       releases it two frames later — the CSS transitions carry it from
       there and the ring only ever crossfades, never blinks */
    const pulseEls = el.querySelectorAll<SVGGraphicsElement>(
      ".halo-sent, .halo-sent .halo-glow, .halo-sent .halo-glow-big, .halo-luna .halo-glow, .halo-luna .halo-glow-big, .orb-glow",
    );
    const setDataState = (next: LunaMarkState | "crescent") => {
      const oldDur = (SPIN_S[el.dataset.state ?? ""] ?? 6) * 1000;
      const newDur = (SPIN_S[next] ?? 6) * 1000;
      const carry: Array<[CSSAnimation, number]> = [];
      if (oldDur !== newDur)
        spins.forEach((s) =>
          cssAnimationsOf(s).forEach((a) => {
            if (typeof a.currentTime === "number")
              carry.push([
                a,
                (((a.currentTime % oldDur) + oldDur) % oldDur) / oldDur,
              ]);
          }),
        );
      pulseEls.forEach((p) => {
        p.style.opacity = getComputedStyle(p).opacity;
      });
      el.dataset.state = next;
      carry.forEach(([a, frac]) => {
        a.currentTime = frac * newDur;
      });
      // commit the frozen values with a forced style flush, then release —
      // the transitions pick up from the committed mid-pulse value. Sync on
      // purpose: an rAF here stalls in throttled/hidden tabs.
      void el.getBoundingClientRect();
      pulseEls.forEach((p) => p.style.removeProperty("opacity"));
    };
    /* step phase animations from one clock position to another at RIDE_RATE —
       normalized to the acting cycle, so a scheduled ride is just as brisk —
       then hand over. Stepping currentTime directly is the only way to
       rewind an infinite animation. Returns a canceller. */
    const ridePhase = (
      anims: CSSAnimation[],
      from: number,
      to: number,
      durMs: number,
      onDone: () => void,
      rate = RIDE_RATE,
    ) => {
      const dist = to - from;
      const rideMs = (Math.abs(dist) / durMs) * ((CYCLE_S * 1000) / rate);
      anims.forEach((a) => {
        a.pause();
        a.currentTime = from;
      });
      let raf: number | undefined;
      const began = performance.now();
      const step = () => {
        const p =
          rideMs === 0 ? 1 : Math.min((performance.now() - began) / rideMs, 1);
        anims.forEach((a) => {
          a.currentTime = from + dist * p;
        });
        if (p < 1) raf = requestAnimationFrame(step);
        else onDone();
      };
      raf = requestAnimationFrame(step);
      return () => {
        if (raf !== undefined) cancelAnimationFrame(raf);
      };
    };
    /* stage the full-cover pose (black terminator over the disc), used by
       slide-in, slide-out and the load-in reveal. transition:none is CRITICAL
       — a leftover inline transition (e.g. WIPE_T from a preceding cycle→
       eclipse) would tween this rx 0→36.5 at cx 60, i.e. the terminator
       IRISES open from the centre. The eclipse must only ever arrive by the
       phase group SLIDING in from the right, never by the rx animating. */
    const stageFullCover = () =>
      parts.forEach((p) => {
        p.style.transition = "none";
        if (p.classList.contains("phase-term")) {
          p.style.setProperty("fill", "#000000");
          p.style.setProperty("rx", "36.5px");
          p.style.setProperty("cx", "60px");
        } else {
          p.style.setProperty("width", "0px");
        }
      });
    /* The negative space lives to the RIGHT of the ball: it advances from
       the right and retreats to the right. HOME and PARKED share the same
       transform function list — mismatched lists interpolate via matrix
       decomposition, which bends a straight slide into an arc. */
    const SLIDE_T = "transform 0.45s cubic-bezier(0.45, 0.05, 0.25, 1)";
    const HOME = "translateX(0px) rotate(11deg)";
    const PARKED = "translateX(150px) rotate(11deg)";
    /* wipe any inline phase styling so the CSS animations own the layer —
       the mirror of the generic thaw, used by the crescent + boot paths */
    const resetPhaseInline = () => {
      if (phase) {
        phase.style.removeProperty("transition");
        phase.style.removeProperty("opacity");
        phase.style.removeProperty("transform");
      }
      parts.forEach((p) => {
        p.style.removeProperty("transition");
        for (const prop of ["fill", "rx", "cx", "x", "width"])
          p.style.removeProperty(prop);
      });
    };

    if (el.dataset.state === state) {
      // Mounted directly in this state — play the load-in once, start any
      // cycle, and set a crescent's frame (parked animations sit at 0).
      let booting = false;
      if (loadIn && !el.classList.contains("booted")) {
        /* ---- load-in: the corona draws itself in (the .boot class arms a
           one-shot dash sweep) while the orb CYCLES into its full moon — the
           boot rules arm the paused phase animations and the driver rides
           the waxing half, new moon → full, at natural cycle speed (the
           sinusoidal keyframes ARE the easing). A crescent mount stops the
           same ride at its own frame; eclipse mounts keep their cover.
           Timers/rides live in refs (not this effect's cleanup) so
           StrictMode's double-mount can't cancel the show; the first real
           transition sweeps them instead. */
        booting = true;
        el.classList.add("booted", "boot");
        const loop = CYCLE_S * 1000;
        if (state === "crescent") {
          bootTimers.current.push(
            window.setTimeout(() => el.classList.remove("boot"), 1000),
          );
          bootRide.current = ridePhase(
            partAnims(),
            NEW_MOON_FRAC * loop,
            CRESCENT_MS,
            loop,
            holdCrescent,
            1,
          );
        } else if (!isEclipseState(state)) {
          // riding lands exactly on the full-moon beat; dropping .boot there
          // destroys the ride's animations on a frame identical to rest
          bootRide.current = ridePhase(
            partAnims(),
            NEW_MOON_FRAC * loop,
            FULL_FRAC * loop,
            loop,
            () => el.classList.remove("boot"),
            1,
          );
        } else {
          bootTimers.current.push(
            window.setTimeout(() => el.classList.remove("boot"), 1000),
          );
        }
      } else if (state === "crescent") holdCrescent();
      if (isPhase && !el.classList.contains("run")) {
        // a booting cycle waits for the entrance ride to land on full before
        // its own cycle takes over from that same frame
        const wait = booting ? (FULL_FRAC - NEW_MOON_FRAC) * CYCLE_S * 1000 + 80 : 740;
        runTimer.current = window.setTimeout(() => el.classList.add("run"), wait);
        return () => window.clearTimeout(runTimer.current);
      }
      return;
    }

    // a real transition takes over from any load-in still in flight
    bootTimers.current.forEach((t) => window.clearTimeout(t));
    bootTimers.current = [];
    bootRide.current?.();
    bootRide.current = undefined;
    el.classList.remove("boot");

    const prev = el.dataset.state;
    // crescent is the acting cycle paused, so leaving it uses the same exit
    // grammar as leaving a running cycle (slide off behind the ball, wipe
    // closed into an eclipse, or unpause into a cycle).
    const wasPhase = isPhaseState(prev) || prev === "crescent";
    const fromEclipse = isEclipseState(prev);
    const toEclipse = isEclipseState(state);
    const timers: number[] = [];
    let steer: (() => void) | undefined;
    let thawNeeded = false;

    /* ---- cancellation arrives by asteroid ------------------------------
       The cancelled flip is DEFERRED to the moment of impact: the mark
       stays fully in its previous state — an acting cycle keeps cycling
       right up to the strike, the ring keeps its colour — so nothing
       betrays the incoming rock. At the boom the real state flip lands
       under the shatter (the state CSS greys the corona and voids the
       ball right then), the debris belt blooms and thins mote by mote,
       and once the last mote burns out the impact attribute is dropped —
       the resting look IS the cancelled pose (void + grey corona), so a
       smashed run and a mounted-cancelled row are identical.
       Skipped (instant flip instead) when: mounting straight into
       cancelled (prev == null), static/reduced-motion renders, or the
       mark is a sub-24px dot — the theatrics are sub-pixel there and
       would only delay the status colour by the flight time. A mid-flight
       state change unwinds via this effect's cleanup. */
    if (
      state === "cancelled" &&
      prev !== undefined &&
      size >= 24 &&
      el.dataset.static !== "true" &&
      !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      el.dataset.impact = "fly";
      let settle: number | undefined;
      const boom = window.setTimeout(() => {
        el.dataset.impact = "boom";
        window.clearTimeout(runTimer.current);
        el.classList.remove("run");
        setDataState(state);
        /* once the last mote has burned out (max die 4.2s + 0.8s fade) the
           belt is pure invisible animation load — drop the attribute so
           every belt animation unmatches; the state CSS already owns the
           void, so nothing changes visually */
        settle = window.setTimeout(() => {
          delete el.dataset.impact;
        }, 5_200);
        /* re-zero the belt's clocks (setDataState just flushed styles, so
           the boom-scoped animations exist by now). The browser RETAINS a
           same-name CSS animation across rule changes — on a replayed
           impact every mote's burn-out would already be finished-forwards
           (= belt instantly dead). WAAPI currentTime is the only exact
           reset, exactly as with the crescent frames. */
        el.querySelectorAll<SVGGraphicsElement>(
          ".dust-orbit circle, .dust-orbit path",
        ).forEach((d) =>
          cssAnimationsOf(d).forEach((a) => {
            a.currentTime = 0;
            a.play();
          }),
        );
      }, FLIGHT_MS);
      return () => {
        window.clearTimeout(boom);
        window.clearTimeout(settle);
      };
    }
    if (el.dataset.impact) delete el.dataset.impact;

    const WIPE_T =
      "rx 0.32s ease-in-out, cx 0.32s ease-in-out, width 0.32s ease-in-out";
    /* two-step wipes must not pause at the meridian: the collapse ACCELERATES
       into it (ease-in) and the sweep-across flies OUT of it (ease-out), a
       matched pair so velocity is continuous through the seam */
    const WIPE_IN =
      "rx 0.32s cubic-bezier(0.5, 0, 1, 1), cx 0.32s cubic-bezier(0.5, 0, 1, 1)";
    const WIPE_OUT =
      "rx 0.32s cubic-bezier(0, 0, 0.5, 1), cx 0.32s cubic-bezier(0, 0, 0.5, 1)";
    const freeze = () =>
      parts.forEach((p) => {
        const c = getComputedStyle(p);
        for (const prop of ["fill", "rx", "cx", "x", "width"] as const) {
          const v = c.getPropertyValue(prop);
          if (v) p.style.setProperty(prop, v);
        }
      });
    const setWipeTransition = () =>
      parts.forEach((p) => {
        p.style.transition = WIPE_T;
      });
    /* the shared second half of every wipe: the terminator, collapsed to an
       invisible line at the meridian, flips paint and sweeps across —
       timed to the collapse's end and eased OUT so it exits the meridian
       at the speed the collapse entered it */
    const flipAndSweep = (fill: string) =>
      timers.push(
        window.setTimeout(() => {
          terms.forEach((t) => {
            t.style.transition = WIPE_OUT;
            t.style.setProperty("fill", fill);
            t.style.setProperty("rx", "36.5px");
            t.style.setProperty("cx", "60px");
          });
        }, 320),
      );

    /* ---- crescent rides the cycle's own clock ------------------------- */
    if (state === "crescent") {
      window.clearTimeout(runTimer.current);
      const loop = CYCLE_S * 1000;
      // where on the (normalized) clock is the disc right now? A live cycle
      // maps its position across; an eclipse sits at its new moon; anything
      // lit rests at full.
      const liveCycle = isPhaseState(prev) && el.classList.contains("run");
      let startCt = 0;
      if (liveCycle) {
        const durMs = (prev === "scheduled" ? SCHEDULED_S : CYCLE_S) * 1000;
        const a = partAnims()[0];
        if (a && typeof a.currentTime === "number") {
          const pos = ((a.currentTime % durMs) + durMs) % durMs;
          startCt = (pos / durMs) * loop;
        }
      } else if (fromEclipse) {
        startCt = NEW_MOON_FRAC * loop;
      }
      el.classList.remove("run");
      resetPhaseInline();
      setDataState("crescent");
      // ride to the crescent point: a live cycle takes the SHORTEST path
      // (rewinding mid-motion is fine); from a resting full disc the dark
      // must enter the stage the legal way — advancing from the right — so
      // the ride is strictly FORWARD through the waning side
      const fwd = (CRESCENT_MS - startCt + loop) % loop;
      const back = loop - fwd;
      const from = startCt + loop; // headroom so a rewind never hits 0
      const to = liveCycle && back < fwd ? from - back : from + fwd;
      const cancel = ridePhase(partAnims(), from, to, loop, holdCrescent);
      return () => {
        cancel();
        holdCrescent();
      };
    }
    if (prev === "crescent" && isPhase) {
      // unpause: the cycle resumes exactly where the crescent froze it —
      // play() also clears the pause() stickiness retained animations keep
      window.clearTimeout(runTimer.current);
      const durMs = (state === "scheduled" ? SCHEDULED_S : CYCLE_S) * 1000;
      setDataState(state);
      el.classList.add("run");
      partAnims().forEach((a) => {
        a.currentTime = CRESCENT_FRAC * durMs;
        a.play();
      });
      return;
    }
    if (prev === "crescent" && !toEclipse) {
      // leaving the pause for any lit state (idle/thinking/done/…): unpause
      // and hurry the cycle to FULL so the light returns through the phase's
      // own motion — never a fade or a rigid slide of the frozen dark chunk.
      // The destination's ball morph starts at hand-over, on a clean disc.
      window.clearTimeout(runTimer.current);
      const loop = CYCLE_S * 1000;
      const cancel = ridePhase(partAnims(), CRESCENT_MS, loop, loop, () => {
        setDataState(state);
      });
      return () => {
        cancel();
        setDataState(state);
      };
    }
    /* ------------------------------------------------------------------- */

    if (isPhaseState(prev) && state === "thinking") {
      // thinking never cuts the ball: the running cycle HURRIES to full via
      // the shortest path (rewinding mid-motion is fine — the dark is
      // already on stage), then the ball's thinking morph takes over on a
      // clean disc. Replaces the old freeze+fade, which read as a dark blob
      // dissolving over the shrinking ball.
      window.clearTimeout(runTimer.current);
      const durMs = (prev === "scheduled" ? SCHEDULED_S : CYCLE_S) * 1000;
      const anims = partAnims();
      const a0 = anims[0];
      const pos =
        a0 && typeof a0.currentTime === "number"
          ? ((a0.currentTime % durMs) + durMs) % durMs
          : 0;
      const from = pos + durMs; // headroom so a rewind never hits 0
      const to = pos <= durMs - pos ? from - pos : from + (durMs - pos);
      const cancel = ridePhase(anims, from, to, durMs, () => {
        el.classList.remove("run");
        setDataState("thinking");
      });
      return () => {
        cancel();
        el.classList.remove("run");
        setDataState("thinking");
      };
    }

    if (wasPhase && !toEclipse) {
      // cycle → idle / a tinted sentiment / the other cycle speed: the
      // negative space moves back off behind the ball, continuing its
      // leftward march; the ball recolours/resizes via CSS meanwhile
      freeze();
      thawNeeded = true;
      if (phase) phase.style.transition = SLIDE_T;
      steer = () => phase?.style.setProperty("transform", PARKED);
    } else if (wasPhase) {
      // cycle → eclipse: continue the wipe. The frozen half-cover holds its
      // side so the motion stays directional; a terminator whose paint
      // matches the destination sweeps to full cover, a mismatched one
      // collapses to the meridian first.
      freeze();
      thawNeeded = true;
      setWipeTransition();
      steer = () => {
        let anyMismatch = false;
        terms.forEach((t) => {
          const isWhite = t.style.getPropertyValue("fill").includes("255");
          t.style.setProperty("cx", "60px");
          if (!isWhite) {
            t.style.setProperty("rx", "36.5px");
          } else {
            t.style.transition = WIPE_IN;
            t.style.setProperty("rx", "0px");
            anyMismatch = true;
          }
        });
        if (anyMismatch) flipAndSweep("#000000");
        el.querySelectorAll<SVGGraphicsElement>(".phase-half-inv").forEach(
          (p) => p.style.setProperty("width", "0px"),
        );
      };
    } else if (fromEclipse && !toEclipse) {
      // eclipse → anything else (lit, tinted, or the cycle): the full-cover
      // dark SLIDES back OUT behind the ball, revealing it — recoloured if
      // a sentiment awaits, and if a cycle awaits, its phases then advance
      // from the right where the dark just went
      thawNeeded = true;
      stageFullCover();
      if (phase) phase.style.transition = SLIDE_T;
      steer = () => phase?.style.setProperty("transform", PARKED);
    } else if (!fromEclipse && toEclipse) {
      // lit → eclipse: the centred dark SLIDES IN from behind the ball and
      // settles over it; the disc fade then hides the ball completely.
      // (Stage without a transition — the transition arrives with the steer,
      // or the stage itself would tween.)
      thawNeeded = true;
      stageFullCover();
      if (phase) phase.style.transform = PARKED;
      steer = () => {
        if (phase) {
          phase.style.transition = SLIDE_T;
          phase.style.setProperty("transform", HOME);
        }
      };
    }
    // eclipse ↔ eclipse, lit ↔ lit, lit → cycle: no phase choreography

    // apply steering targets a frame after the setup/frozen pose committed,
    // so the inline transitions tween from it. Declared before the thaw so
    // the thaw can cancel a steer that never got to run — rAF is paused in
    // background tabs, and a steer firing AFTER the thaw would strand stale
    // inline styles.
    let raf1: number | undefined;
    let raf2: number | undefined;

    let phaseThaw: number | undefined;
    if (thawNeeded) {
      phaseThaw = window.setTimeout(() => {
        if (raf1 !== undefined) cancelAnimationFrame(raf1);
        if (raf2 !== undefined) cancelAnimationFrame(raf2);
        if (phase) {
          phase.style.removeProperty("transition");
          phase.style.removeProperty("opacity");
          phase.style.removeProperty("transform");
        }
        parts.forEach((p) => {
          p.style.removeProperty("transition");
          for (const prop of ["fill", "rx", "cx", "x", "width"])
            p.style.removeProperty(prop);
        });
      }, 800);
    }

    window.clearTimeout(runTimer.current);
    el.classList.remove("run");
    setDataState(state);

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => steer?.());
    });

    if (isPhase) {
      // from an eclipse or the other cycle speed, wait for the wipe to open
      // back to full moon before the cycle starts
      const wait = fromEclipse || wasPhase ? 780 : 200;
      runTimer.current = window.setTimeout(() => el.classList.add("run"), wait);
    }

    return () => {
      if (raf1 !== undefined) cancelAnimationFrame(raf1);
      if (raf2 !== undefined) cancelAnimationFrame(raf2);
      window.clearTimeout(runTimer.current);
      window.clearTimeout(phaseThaw);
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [state, loadIn]);

  /* IDLE-MOTION SUSPENSION — the mark's continuous motions (.surface scroll,
     .halo-spin rotation, the glow pulses) are compositor animations, and while
     ANY compositor animation runs Chrome produces a full window frame every
     vsync, forever. That bill is per-window, not per-pixel: it barely depends
     on how big or how many the animated layers are, which is why a mark this
     small can cost ~40% of a core on a 1440p @ 165Hz display, on every route,
     for as long as a tab stays open.

     Chrome already throttles HIDDEN tabs, but not a visible tab in an
     UNFOCUSED window — an app parked behind a video call, which is exactly
     where this was found and hurts most. So: suspend while the window has no
     focus (and while the tab is hidden, which costs nothing to add and does
     not rely on Chrome's throttling heuristics), resume on focus.

     Law 6 — WAAPI, never animation-play-state: pause()/play() preserve
     currentTime exactly, so the corona resumes on its own angle and the
     surface on its own offset. Law 2 holds: the spin is never paused,
     retimed or reset BY A STATE CHANGE, and never lands on a new frame.

     Only animations this effect actually paused are resumed, so layers that
     are deliberately paused elsewhere — the `infinite paused` phase
     construction, a held crescent — stay frozen. Rides started while blurred
     are rAF-driven (law 6) and complete normally; they are transient. */
  useEffect(() => {
    const el = svgRef.current;
    if (!el || el.dataset.static === "true") return;
    if (typeof el.getAnimations !== "function") return;

    let suspended: Animation[] = [];

    const suspend = () => {
      if (suspended.length) return;
      suspended = el
        .getAnimations({ subtree: true })
        .filter((a) => a.playState === "running");
      suspended.forEach((a) => a.pause());
    };
    const resume = () => {
      suspended.forEach((a) => {
        if (a.playState === "paused") a.play();
      });
      suspended = [];
    };
    const sync = () =>
      document.hasFocus() && !document.hidden ? resume() : suspend();

    sync();
    window.addEventListener("focus", resume);
    window.addEventListener("blur", suspend);
    document.addEventListener("visibilitychange", sync);
    return () => {
      window.removeEventListener("focus", resume);
      window.removeEventListener("blur", suspend);
      document.removeEventListener("visibilitychange", sync);
      resume();
    };
  }, [animated]);

  /* Law 8 — hand this mark's continuous motions to the shared frame governor
     (ds/frameGovernor). Guarded exactly like the driver: a non-browser DOM
     (jsdom) has no WAAPI, so the mark simply renders and the governor never
     engages. Reduced motion also skips registration — the media rule already
     removes every continuous animation, so ticking would be a standing
     polling workload with nothing to advance, for exactly the users who
     asked for no motion. */
  useEffect(() => {
    const el = svgRef.current;
    if (!el || !animated) return;
    if (
      typeof el.getAnimations !== "function" ||
      typeof CSSAnimation === "undefined"
    )
      return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches)
      return;
    return governMotion(el, size >= HERO_PX);
  }, [animated, size]);

  return (
    <chakra.span
      {...dsTag("LunaMark")}
      ref={svgRef}
      className="luna"
      data-state={initialState.current}
      data-static={animated ? undefined : "true"}
      data-size={size <= 24 ? "sm" : undefined}
      role="img"
      aria-label={label ?? STATE_LABELS[state]}
      sx={{ ...lunaSx, width: `${size}px`, height: `${size}px` }}
    >
      <span className="orb-body">
      <svg
        className="luna-body"
        width={`${size}px`}
        height={`${size}px`}
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
      <defs>
        {/* Literal stops below are the asset's artwork values (handover):
            pole mid-tone, orb shading ramp, limb darkening, corona highlight.
            Brand endpoints resolve to the luna palette. */}
        <linearGradient id={`pole-${id}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--chakra-colors-luna-100)" />
          <stop offset="50%" stopColor="#6E77E8" />
          <stop offset="100%" stopColor="var(--chakra-colors-luna-100)" />
        </linearGradient>
        <radialGradient id={`orb-${id}`} cx="34%" cy="28%" r="90%">
          <stop offset="0%" stopColor="#B0B6F2" />
          <stop offset="45%" stopColor="#666FD9" />
          <stop offset="100%" stopColor="#4C53BE" />
        </radialGradient>
        <radialGradient id={`limb-${id}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#000000" stopOpacity="0" />
          <stop offset="62%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#2E3384" stopOpacity="0.42" />
        </radialGradient>
        <radialGradient id={`spec-${id}`} cx="31%" cy="25%" r="42%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`sshade-${id}`} cx="34%" cy="28%" r="95%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.38" />
          <stop offset="45%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="72%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.22" />
        </radialGradient>
        <linearGradient id={`halo-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#9BA2F3" />
          <stop offset="55%" stopColor="var(--chakra-colors-luna-0)" />
          <stop offset="100%" stopColor="var(--chakra-colors-luna-100)" />
        </linearGradient>
        <filter id={`blur-${id}`} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="3.2" />
        </filter>
        <filter id={`blurBig-${id}`} x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="7" />
        </filter>
        <filter id={`term-${id}`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.4" />
        </filter>
        {/* the terminator's core edge: crisp against the wide penumbra
            shading layered underneath (horn softness comes from the penumbra
            and the cx drift, not from this blur) */}
        <filter id={`pblur-${id}`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="1.8" />
        </filter>
        <filter id={`surf-${id}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.5" />
        </filter>
        {/* The negative-space alpha mask: white keeps the disc, the phase
            construction below cuts it to genuine transparency.

            Referenced from CSS on .disc (an HTML element) rather than an SVG
            mask attribute, because the disc is now a stack of layers so the
            moving surface can composite. userSpaceOnUse coordinates resolve
            against the referencing element's CSS box, NOT the 120-unit viewBox
            — so the region is in px and the construction is scaled into place,
            leaving every coordinate and keyframe below authored in 120-space. */}
        <mask
          id={`phase-${id}`}
          maskUnits="userSpaceOnUse"
          x={-30 * maskScale}
          y={-30 * maskScale}
          width={180 * maskScale}
          height={180 * maskScale}
        >
          <rect
            x={-30 * maskScale}
            y={-30 * maskScale}
            width={180 * maskScale}
            height={180 * maskScale}
            fill="#FFFFFF"
          />
          {/* the negative-space construction: black half-cover + terminator
              ellipse, in THREE copies of the same animated geometry — a
              wide half-strength penumbra (spherical grazing shade), a tight
              core edge, and the blurred LIT-shape overlay (graded
              earthshine). This one construction forms the lunar cycle AND
              every eclipse pose. */}
          <g transform={`scale(${maskScale})`}>
          <g className="phase">
            <g filter={`url(#blurBig-${id})`} opacity="0.45">
              <rect className="phase-half" x="60" y="16" width="44" height="88" fill="#000000" />
              <ellipse className="phase-term" cx="60" cy="60" rx="36.5" ry="36.5" fill="#FFFFFF" />
            </g>
            <g filter={`url(#pblur-${id})`}>
              <rect className="phase-half" x="60" y="16" width="44" height="88" fill="#000000" />
              <ellipse className="phase-term" cx="60" cy="60" rx="36.5" ry="36.5" fill="#FFFFFF" />
            </g>
            {/* graded earthshine: the blurred LIT shape at low strength.
                White over the lit side (no-op); its soft edge lifts the dark
                side to ~20% at the terminator, decaying to the group's 5%
                baseline at the limb. The ellipse shares .phase-term. */}
            <g filter={`url(#blurBig-${id})`} opacity="0.3">
              <rect className="phase-half-inv" x="16" y="16" width="44" height="88" fill="#FFFFFF" />
              <ellipse className="phase-term" cx="60" cy="60" rx="36.5" ry="36.5" fill="#FFFFFF" />
            </g>
          </g>
          </g>
        </mask>
      </defs>

      {/* orb body: disc + phase mask scale together — the mask rides .disc, so
          .orb-body's 0.8 scale carries both */}
        {/* thinking's pulsing bloom — behind the disc, unmasked so it halos */}
        <circle className="orb-glow" cx="60" cy="60" r="34" fill={`url(#halo-${id})`} filter={`url(#blurBig-${id})`} />
      </svg>

      {/* The disc is a stack of sibling layers rather than one group, so the
          scrolling surface can be an animated HTML element and reach the
          compositor. The circular clip and the phase mask both live on this
          wrapper — one mask over the whole stack, as the single <g> had — and
          masking an ancestor does not cost the child its compositing. */}
      <span
        className="disc"
        style={{
          mask: `url(#phase-${id})`,
          WebkitMask: `url(#phase-${id})`,
        }}
      >
        <svg
          className="disc-base"
          width={`${size}px`}
          height={`${size}px`}
          viewBox="0 0 120 120"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <circle cx="60" cy="60" r="34" fill={`url(#orb-${id})`} />
        </svg>

        {/* The scroll animates the SPAN, not the svg: Chrome will not run a
            transform animation on an SVG element on the compositor — verified
            by bisect, it stays main-thread however it is hinted. An HTML
            element with static svg content inside does composite. */}
        <span className="surface">
          <svg
            width={`${size}px`}
            height={`${size}px`}
            viewBox="0 0 120 120"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <g filter={`url(#surf-${id})`}>
              <rect x="-48" y="12" width="96" height="96" fill={`url(#pole-${id})`} />
              <rect x="48" y="12" width="96" height="96" fill={`url(#pole-${id})`} />
              <rect x="144" y="12" width="96" height="96" fill={`url(#pole-${id})`} />
            </g>
          </svg>
        </span>

        <svg
          className="disc-shade"
          width={`${size}px`}
          height={`${size}px`}
          viewBox="0 0 120 120"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          {/* the sentiment tint recolours THE ball with the soft flat finish
              of the old sentiment disc (colour + one gentle shade); the
              sphere shading below fades out with it so the tinted ball
              doesn't read overly dimensional */}
          <g className="tint">
            <circle className="tint-fill" cx="60" cy="60" r="34" />
            <circle cx="60" cy="60" r="34" fill={`url(#sshade-${id})`} opacity="0.85" />
          </g>
          <circle className="shade-limb" cx="60" cy="60" r="34" fill={`url(#limb-${id})`} />
          <circle className="shade-spec" cx="60" cy="60" r="34" fill={`url(#spec-${id})`} />
        </svg>
      </span>
      </span>

      {/* corona: one spinning ring, luna gradient crossfades to sentiment.
          The rotation animates the SPAN rather than the svg inside it, for the
          same reason as .surface above — an SVG element's transform animation
          never reaches the compositor. Rotating this wrapper is the rotation
          the <g> used to do, since the corona's circles are all centred on the
          viewBox and the wrapper is square. */}
      <span className="halo-spin">
      <svg
        width={`${size}px`}
        height={`${size}px`}
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <g className="halo-luna">
          <circle
            cx="60"
            cy="60"
            r="45"
            fill="none"
            stroke={`url(#halo-${id})`}
            filter={`url(#blurBig-${id})`}
            opacity="0.5"
            className="halo-glow-big"
          />
          <circle
            cx="60"
            cy="60"
            r="45"
            fill="none"
            stroke={`url(#halo-${id})`}
            filter={`url(#blur-${id})`}
            opacity="0.7"
            className="halo-glow"
          />
          <circle cx="60" cy="60" r="45" fill="none" stroke={`url(#halo-${id})`} className="halo-ring" />
        </g>
        <g className="halo-sent">
          <circle cx="60" cy="60" r="45" fill="none" filter={`url(#blurBig-${id})`} opacity="0.45" className="halo-glow-big" />
          <circle cx="60" cy="60" r="45" fill="none" filter={`url(#blur-${id})`} opacity="0.65" className="halo-glow" />
          <circle cx="60" cy="60" r="45" fill="none" className="halo-ring" />
        </g>
      </svg>
      </span>

      {/* Third layer purely to preserve paint order: these two sat ABOVE the
          corona in the original single-svg document order, and the impact
          animation crosses it. */}
      <svg
        className="luna-over"
        width={`${size}px`}
        height={`${size}px`}
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
      {/* sentiment layer: only blocked's glowing annulus lives here */}
      <g className="sent">
        <g className="sent-ring">
          <circle cx="60" cy="60" r="34" fill="none" strokeWidth="7" filter={`url(#blurBig-${id})`} opacity="0.55" />
          <circle cx="60" cy="60" r="34" fill="none" strokeWidth="4" filter={`url(#blur-${id})`} opacity="0.75" />
          <circle cx="60" cy="60" r="34" fill="none" strokeWidth="2.5" />
        </g>
      </g>

      {/* the asteroid impact — dormant unless data-impact is staged.
          Outer g carries the static centre translate (attribute transform);
          CSS animates the nested flight/tumble groups so they never fight. */}
      <g className="impact-layer">
        {/* aftermath debris belt: every mote orbits the void on its own
            period; negative delays scatter the start angles */}
        <g className="debris">
          {DUST.map((p, i) => (
            <g
              key={`d${i}`}
              className="dust-orbit"
              style={{ "--dur": `${p.dur}s`, "--o": p.o, "--tw": `${-p.frac * 2.4}s`, "--die": `${p.die}s`, animationDelay: `${-p.frac * p.dur}s` } as React.CSSProperties}
            >
              <circle
                cx={60 + p.r}
                cy="60"
                r={p.s}
                fill={p.grey ? "var(--chakra-colors-neutral-500)" : "#7C84EC"}
              />
            </g>
          ))}
          {CHUNKS.map((c, i) => (
            <g
              key={`c${i}`}
              className="dust-orbit"
              style={{ "--dur": `${c.dur}s`, "--o": c.o, "--tw": `${-c.frac * 2.4}s`, "--die": `${c.die}s`, animationDelay: `${-c.frac * c.dur}s` } as React.CSSProperties}
            >
              <path
                d={c.d}
                transform={`translate(${60 + c.r} 60)`}
                fill="var(--chakra-colors-neutral-500)"
              />
            </g>
          ))}
        </g>

        {/* impact burst: shockwave ring + sparks + ball shards, all from
            the orb's heart */}
        <circle className="shockwave" cx="60" cy="60" r="12" stroke="var(--chakra-colors-neutral-400)" strokeWidth="2" />
        <g className="sparks">
          {SPARKS.map((s, i) => (
            <circle
              key={i}
              cx="60"
              cy="60"
              r={s.s}
              fill={s.hot ? "var(--chakra-colors-luna-100)" : "#B0B6F2"}
              style={{ "--dx": `${s.dx}px`, "--dy": `${s.dy}px`, "--dur": `${s.dur}s` } as React.CSSProperties}
            />
          ))}
        </g>
        <g className="shards" transform="translate(60 60)">
          {SHARDS.map((s, i) => (
            <path
              key={i}
              d={s.d}
              fill={i % 2 ? "var(--chakra-colors-neutral-400)" : "var(--chakra-colors-neutral-500)"}
              style={{ "--dx": `${s.dx}px`, "--dy": `${s.dy}px`, "--dr": `${s.dr}deg` } as React.CSSProperties}
            />
          ))}
        </g>

        {/* the rock: Asteroids-game vector outline, tumbling in with a
            flickering exhaust trail (trail rides the flight group) */}
        <g transform="translate(60 60)">
          <g className="asteroid-flight">
            <g className="asteroid-trail">
              <circle cx="10" cy="-9" r="1.8" fill="var(--chakra-colors-neutral-500)" />
              <circle cx="17" cy="-15" r="1.4" fill="var(--chakra-colors-neutral-400)" />
              <circle cx="25" cy="-22" r="1" fill="var(--chakra-colors-neutral-400)" />
            </g>
            <g className="asteroid-tumble">
              <path
                d="M-9 -3 L-5 -9 L1 -10 L7 -6 L10 -1 L6 4 L7 8 L1 10 L-6 8 L-10 2 Z"
                fill="var(--chakra-colors-neutral-600)"
                stroke="var(--chakra-colors-neutral-300)"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
              {/* craters, per the arcade look */}
              <circle cx="-3" cy="-2" r="1.6" fill="none" stroke="var(--chakra-colors-neutral-400)" strokeWidth="0.8" />
              <circle cx="3" cy="4" r="1" fill="none" stroke="var(--chakra-colors-neutral-400)" strokeWidth="0.8" />
            </g>
          </g>
        </g>
      </g>
      </svg>
    </chakra.span>
  );
};

